import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraActivityFeed, SpecraCompareColumns, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraHeading, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraDateField, SpecraEntityMatchConfirm, SpecraFormField, SpecraInlineBanner, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ReviewAction, ReviewDecision } from '../../../../../../gen/ts/mono/datagateway/v1/registry_records_pb';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient } from '../../../lib/connect/clients';
import { ErrorState } from '../../Delivery/_shared/components/PageStates';
import { CandidateDecisionPanel } from '../../Delivery/Registries/CandidateDecisionPanel';
import { registryKeys } from '../queryKeys';
import { SUGGESTER_LABEL } from '../records/lcat/model';
import { decide } from './decide';
import { EmploymentChangeItem } from './EmploymentChangeItem';
const side = (value, id)=>({
        id,
        eyebrow: value?.eyebrow ?? '',
        title: value?.title,
        rows: value?.fields.map((f)=>({
                id: f.key,
                label: f.label,
                value: f.value || '—',
                state: f.match === 'SAME' ? 'same' : f.match === 'DIFFERENT' ? 'different' : 'neutral'
            }))
    });
const parse = (d)=>JSON.parse(d.actions[0]?.payloadJson || '{}');
function CandidateControls({ item, busy, onAccept, onReject }) {
    const { user } = useAuth();
    const data = useQuery({
        queryKey: [
            ...registryKeys.all,
            'review-candidate-controls',
            user?.tenant_id,
            user?.id,
            item.id
        ],
        queryFn: async ()=>{
            const companyId = user?.tenant_id ?? '';
            const [candidates, entries, companies, vehicles] = await Promise.all([
                deliveryClient.listLaborCategoryCandidates({
                    companyId,
                    status: 'PENDING'
                }),
                deliveryClient.listLaborCategoryEntries({
                    companyId
                }),
                deliveryClient.listCompanyProfiles({
                    companyId
                }),
                deliveryClient.listContractVehicles({
                    companyId
                })
            ]);
            return {
                candidate: candidates.candidates.find((c)=>c.id === item.id),
                entries: entries.entries,
                companies: companies.profiles,
                vehicles: vehicles.vehicles
            };
        },
        retry: false
    });
    if (data.isPending) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading correction and resolution options…"
    });
    if (data.isError) return /*#__PURE__*/ _jsx(ErrorState, {
        error: data.error,
        onRetry: ()=>void data.refetch()
    });
    if (!data.data.candidate) return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "warning",
        children: "This candidate is no longer available. Reload the queue."
    });
    return /*#__PURE__*/ _jsx(CandidateDecisionPanel, {
        candidate: data.data.candidate,
        entries: data.data.entries,
        companies: data.data.companies,
        vehicles: data.data.vehicles,
        canEdit: true,
        busy: busy,
        onAccept: onAccept,
        onReject: onReject
    });
}
export function ReviewDetail({ item, onRecorded }) {
    if (item.kind === 'EMPLOYMENT_CHANGE') return /*#__PURE__*/ _jsx(EmploymentChangeItem, {
        item: item,
        onRecorded: ()=>onRecorded()
    });
    return /*#__PURE__*/ _jsx(GenericReviewDetail, {
        item: item,
        onRecorded: onRecorded
    });
}
function GenericReviewDetail({ item, onRecorded }) {
    const [busy, setBusy] = useState(false);
    const pending = useRef(false);
    const [error, setError] = useState('');
    const [active, setActive] = useState();
    const [inputs, setInputs] = useState({});
    const [resume, setResume] = useState();
    const [declined, setDeclined] = useState(false);
    const run = async (decision, values, progress = {
        step: 0
    })=>{
        if (pending.current) return;
        pending.current = true;
        setBusy(true);
        setError('');
        try {
            await decide(decision, values, progress, (p)=>setResume({
                    decision,
                    inputs: values,
                    progress: p
                }));
            setResume(undefined);
            setActive(undefined);
            await onRecorded(decision.id === 'SET_CONTRACT_START');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not record that decision. Try again.');
        } finally{
            pending.current = false;
            setBusy(false);
        }
    };
    const decisions = item.decisions;
    const choose = (chosen)=>{
        const decision = chosen.id === 'ALSO_LINK_SIBLING' ? new ReviewDecision({
            ...chosen,
            actions: [
                ...item.decisions.find((d)=>d.id === 'APPROVE_EQUIVALENT')?.actions ?? [],
                ...chosen.actions
            ]
        }) : chosen;
        if (busy || resume && resume.progress.step > 0) return;
        setInputs({});
        setError('');
        const needsInput = decision.actions.some((a)=>a.inputFields.length > 0);
        if (decision.destructive || needsInput) setActive(decision);
        else void run(decision, {});
    };
    const lcatAccept = (args)=>{
        const payload = typeof args === 'string' ? {
            id: args
        } : args;
        void run(new ReviewDecision({
            id: 'ACCEPT',
            actions: [
                new ReviewAction({
                    rpc: '/mono.ryugu.v1.DeliveryService/AcceptLaborCategoryCandidate',
                    payloadJson: JSON.stringify(payload)
                })
            ]
        }), {});
    };
    const reject = item.decisions.find((d)=>d.id === 'REJECT');
    const confirmChoices = item.decisions.filter((d)=>d.id === 'CONFIRM');
    const create = item.decisions.find((d)=>d.id === 'CREATE_NEW');
    const entityKind = item.right?.ref?.kind;
    const useEntityPrompt = item.kind === 'ENTITY_MATCH' && confirmChoices.length > 0 && !!entityKind && [
        'CONTACT',
        'COMPANY',
        'GOVERNMENT_ORG',
        'VEHICLE'
    ].includes(entityKind);
    const locked = busy || !!(resume && resume.progress.step > 0);
    const fields = [
        ...new Set(active?.actions.flatMap((a)=>a.inputFields) ?? [])
    ].filter((f)=>!f.includes('.'));
    const dateFields = [
        'start',
        'end',
        'contractStart'
    ];
    const valid = fields.every((f)=>!!inputs[f]?.trim()) && (!inputs.start || !inputs.end || inputs.start <= inputs.end);
    const values = Object.fromEntries(Object.entries(inputs).map(([key, value])=>[
            key,
            dateFields.includes(key) ? `${value}T00:00:00Z` : value.trim()
        ]));
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4 p-4",
        "aria-busy": busy,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        children: item.kind.split('_').join(' ')
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            item.sourceLabel,
                            item.confidenceBand && !item.sourceLabel.startsWith(SUGGESTER_LABEL) ? ` · confidence ${item.confidence.toFixed(2)}` : ''
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraHeading, {
                level: 2,
                size: "ui",
                "data-testid": Testids.registryReview.question,
                children: item.question
            }),
            /*#__PURE__*/ _jsx(SpecraCompareColumns, {
                "data-testid": Testids.registryReview.compare,
                framed: "cards",
                sides: [
                    side(item.left, 'left'),
                    side(item.right, 'right')
                ]
            }),
            item.alternatives.map((alt, index)=>/*#__PURE__*/ _jsx(SpecraCompareColumns, {
                    framed: "cards",
                    sides: [
                        side(item.left, 'observed'),
                        side(alt, `alternative-${index}`)
                    ]
                }, alt.ref?.id ?? index)),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.registryReview.evidence,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Evidence"
                    }),
                    /*#__PURE__*/ _jsx(SpecraActivityFeed, {
                        items: item.evidence.map((e, i)=>({
                                id: String(i),
                                title: e.text
                            }))
                    })
                ]
            }),
            item.warning && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                "data-testid": Testids.registryReview.warning,
                variant: "framed",
                tone: "warning",
                children: item.warning
            }),
            error && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    resume && resume.progress.step > 0 ? 'The first decision was saved; the next step failed — retry. ' : '',
                    error,
                    resume && resume.progress.step > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                        disabled: busy,
                        onClick: ()=>void run(resume.decision, resume.inputs, resume.progress),
                        children: "Retry remaining steps"
                    })
                ]
            }),
            item.kind === 'LCAT_CANDIDATE' && /*#__PURE__*/ _jsx(CandidateControls, {
                item: item,
                busy: locked,
                onAccept: lcatAccept,
                onReject: ()=>reject && choose(new ReviewDecision({
                        ...reject,
                        destructive: true
                    }))
            }),
            useEntityPrompt && /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
                density: "card",
                kind: entityKind,
                observedLabel: item.left?.title ?? item.title,
                observedSource: item.sourceLabel,
                question: item.question,
                state: busy ? 'pending' : error ? 'error' : declined ? 'declined' : 'ask',
                errorMessage: error,
                disabled: locked,
                candidates: confirmChoices.map((d, i)=>{
                    const p = parse(d);
                    const s = [
                        item.right,
                        ...item.alternatives
                    ][i];
                    return {
                        id: p.chosenRegistryId || p.chosenPublicKey,
                        name: s?.title ?? d.label,
                        details: s?.fields.map((f)=>({
                                label: f.label,
                                value: f.value
                            }))
                    };
                }),
                onConfirm: (id)=>{
                    const d = confirmChoices.find((d)=>{
                        const p = parse(d);
                        return (p.chosenRegistryId || p.chosenPublicKey) === id;
                    });
                    if (d) choose(d);
                },
                onDecline: ()=>create ? setDeclined(true) : reject && choose(reject),
                onCreateNew: create ? ()=>choose(create) : undefined
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-2",
                children: decisions.filter((d)=>!(item.kind === 'LCAT_CANDIDATE' && [
                        'ACCEPT',
                        'CORRECT_AND_ACCEPT',
                        'RESOLVE_TO_EXISTING',
                        'REJECT'
                    ].includes(d.id)) && !(useEntityPrompt && [
                        'CONFIRM',
                        'CREATE_NEW',
                        'REJECT'
                    ].includes(d.id))).map((d, i)=>/*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.registryReview.decision,
                        "data-decision-id": d.id,
                        variant: d.destructive ? 'destructive' : d.primary ? 'default' : 'secondary',
                        disabled: locked || d.id === 'MERGE_DUPLICATE',
                        onClick: ()=>choose(d),
                        children: d.label
                    }, `${d.id}-${i}`))
            }),
            item.decisions.some((d)=>d.id === 'MERGE_DUPLICATE') && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                children: "Merge is unavailable here: this queue item does not identify the created record to merge away. Review the records from their registry pages."
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "body-ui",
                tone: "muted",
                "data-testid": Testids.registryReview.consequence,
                children: item.consequenceNote
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: !!active,
                onOpenChange: (open)=>{
                    if (!busy && !open) setActive(undefined);
                },
                title: active?.label ?? 'Record decision',
                size: "sm",
                footerProps: {
                    confirmLabel: 'Record decision',
                    loading: busy,
                    confirmDisabled: !valid || locked,
                    onConfirm: ()=>active && void run(active, values),
                    onCancel: ()=>setActive(undefined),
                    confirmProps: {
                        'data-testid': Testids.registryReview.confirm
                    },
                    cancelProps: {
                        disabled: busy
                    }
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-3",
                    children: [
                        active?.destructive && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "This decision will be recorded. Confirm to continue."
                        }),
                        fields.map((f)=>dateFields.includes(f) ? /*#__PURE__*/ _jsx(SpecraDateField, {
                                label: f === 'contractStart' ? 'Contract start' : f === 'start' ? 'Start' : 'End (inclusive)',
                                picker: "native",
                                value: inputs[f] ?? '',
                                onValueChange: (value)=>setInputs((p)=>({
                                            ...p,
                                            [f]: value
                                        })),
                                disabled: locked,
                                required: true
                            }, f) : /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: f === 'reviewNote' ? 'Review note' : 'Reason',
                                required: true,
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    value: inputs[f] ?? '',
                                    disabled: locked,
                                    onChange: (e)=>setInputs((p)=>({
                                                ...p,
                                                [f]: e.target.value
                                            })),
                                    required: true
                                })
                            }, f)),
                        inputs.start && inputs.end && inputs.start > inputs.end && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "End must be on or after start."
                        }),
                        error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "danger",
                            children: error
                        })
                    ]
                })
            })
        ]
    });
}
